import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, ModalShell } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type GastoEstado = 'pendiente' | 'aprobado' | 'rechazado';
type GastoOrigen = 'recurrente' | 'puntual';

interface BalanceSummary {
  facturado: number;
  cobrado: number;
  adeudado: number;
  gastado: number;
  gastadoPendiente: number;
  resultado: number;
  gastosAprobados: number;
  gastosPendientes: number;
}

interface PanelGasto {
  id: number;
  nombre: string;
  categoriaId: number | null;
  categoria: string;
  monto: number;
  estado: GastoEstado;
  origen: GastoOrigen;
  notas: string | null;
}

interface PanelGastoCategoria {
  id: number;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
}

const money = (n: number) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(n || 0);

const emptyGastoForm = () => ({
  nombre: '',
  categoriaId: '',
  categoria: '',
  monto: '',
  notas: '',
  aprobarAlCrear: true,
  esRecurrente: false,
});

interface PeriodExpensesSectionProps {
  periodo: string;
  canEdit: boolean;
  compact?: boolean;
  embedded?: boolean;
  hideHeader?: boolean;
  onChanged?: () => void;
}

export const PeriodExpensesSection: React.FC<PeriodExpensesSectionProps> = ({
  periodo,
  canEdit,
  compact = false,
  embedded = false,
  hideHeader = false,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<BalanceSummary | null>(null);
  const [gastos, setGastos] = useState<PanelGasto[]>([]);
  const [categorias, setCategorias] = useState<PanelGastoCategoria[]>([]);
  const [gastoForm, setGastoForm] = useState(emptyGastoForm);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGasto, setEditingGasto] = useState<PanelGasto | null>(null);
  const [deletingGasto, setDeletingGasto] = useState<PanelGasto | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [sortField, setSortField] = useState<'nombre' | 'monto' | 'estado' | 'categoria'>('nombre');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const load = useCallback(async () => {
    if (!periodo || !canEdit) return;
    setIsLoading(true);
    try {
      const [summaryRes, gastosRes, categoriasRes] = await Promise.all([
        httpClient.get<BalanceSummary>(`/balance/summary?periodo=${encodeURIComponent(periodo)}`),
        httpClient.get<PanelGasto[]>(`/balance/gastos?periodo=${encodeURIComponent(periodo)}`),
        httpClient.get<PanelGastoCategoria[]>('/balance/gasto-categorias'),
      ]);
      setSummary(summaryRes.data);
      setGastos(Array.isArray(gastosRes.data) ? gastosRes.data : []);
      setCategorias(Array.isArray(categoriasRes.data) ? categoriasRes.data : []);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.billing.expenses.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [periodo, canEdit, showError, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canEdit) return null;

  const handleSort = (field: 'nombre' | 'monto' | 'estado' | 'categoria') => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortIcon = (field: 'nombre' | 'monto' | 'estado' | 'categoria') => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedGastos = [...gastos].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    let cmp = 0;
    switch (sortField) {
      case 'nombre':
        cmp = a.nombre.localeCompare(b.nombre, 'es');
        break;
      case 'categoria':
        cmp = a.categoria.localeCompare(b.categoria, 'es');
        break;
      case 'monto':
        cmp = a.monto - b.monto;
        break;
      case 'estado':
        cmp = a.estado.localeCompare(b.estado, 'es');
        break;
    }
    return cmp * dir;
  });

  const categoriaLegacy = gastoForm.categoria.trim() &&
    !categorias.some((categoria) => categoria.nombre === gastoForm.categoria.trim())
    ? gastoForm.categoria.trim()
    : '';

  const closeGastoModal = () => {
    setShowAddModal(false);
    setEditingGasto(null);
    setGastoForm(emptyGastoForm());
  };

  const openCreateModal = () => {
    setEditingGasto(null);
    setGastoForm(emptyGastoForm());
    setShowAddModal(true);
  };

  const openEditModal = (gasto: PanelGasto) => {
    setEditingGasto(gasto);
    setGastoForm({
      nombre: gasto.nombre,
      categoriaId: gasto.categoriaId ? String(gasto.categoriaId) : '__legacy',
      categoria: gasto.categoria,
      monto: String(gasto.monto),
      notas: gasto.notas || '',
      aprobarAlCrear: gasto.estado === 'aprobado',
      esRecurrente: gasto.origen === 'recurrente',
    });
    setShowAddModal(true);
  };

  const handleSaveGasto = async (e: React.FormEvent) => {
    e.preventDefault();
    const monto = Number(gastoForm.monto);
    const selectedCategoria = categorias.find(
      (categoria) => String(categoria.id) === gastoForm.categoriaId
    );
    const categoriaNombre = selectedCategoria?.nombre || gastoForm.categoria.trim();
    const categoriaId = selectedCategoria?.id ?? null;
    if (!gastoForm.nombre.trim() || !categoriaNombre || !(monto > 0)) {
      showError(t('pages.billing.expenses.formInvalid'));
      return;
    }
    setIsSaving(true);
    try {
      if (editingGasto) {
        await httpClient.put(`/balance/gastos/${editingGasto.id}`, {
          nombre: gastoForm.nombre.trim(),
          categoriaId,
          categoria: categoriaNombre,
          monto,
          notas: gastoForm.notas.trim() || null,
        });
        success(t('pages.billing.expenses.updated'));
      } else {
        await httpClient.post('/balance/gastos', {
          periodo,
          nombre: gastoForm.nombre.trim(),
          categoriaId,
          categoria: categoriaNombre,
          monto,
          notas: gastoForm.notas.trim() || null,
          aprobarAlCrear: gastoForm.aprobarAlCrear,
          esRecurrente: gastoForm.esRecurrente,
        });
        success(t('pages.billing.expenses.created'));
      }
      closeGastoModal();
      await load();
      onChanged?.();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.billing.expenses.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAprobar = async (id: number) => {
    setBusyId(id);
    try {
      await httpClient.post(`/balance/gastos/${id}/aprobar`);
      success(t('pages.billing.expenses.approved'));
      await load();
      onChanged?.();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.billing.expenses.saveError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleRechazar = async (id: number) => {
    setBusyId(id);
    try {
      await httpClient.post(`/balance/gastos/${id}/rechazar`);
      success(t('pages.billing.expenses.rejected'));
      await load();
      onChanged?.();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.billing.expenses.saveError'));
    } finally {
      setBusyId(null);
    }
  };

  const openDeleteModal = (gasto: PanelGasto) => {
    setDeletingGasto(gasto);
    setDeleteReason('');
  };

  const closeDeleteModal = () => {
    setDeletingGasto(null);
    setDeleteReason('');
  };

  const handleDeleteGasto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deletingGasto) return;
    const motivo = deleteReason.trim();
    if (!motivo) {
      showError(t('pages.billing.expenses.deleteReasonRequired'));
      return;
    }

    setBusyId(deletingGasto.id);
    try {
      await httpClient.delete(`/balance/gastos/${deletingGasto.id}`, {
        data: { motivo },
      });
      success(t('pages.billing.expenses.deleted'));
      closeDeleteModal();
      await load();
      onChanged?.();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.billing.expenses.saveError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className={`space-y-3 ${
        embedded
          ? 'p-3 xl:p-4'
          : compact
            ? 'bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 xl:p-4'
            : ''
      }`}
    >
      {!hideHeader ? (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3
            className={`font-semibold text-gray-900 dark:text-white ${
              compact ? 'text-base' : 'text-lg'
            }`}
          >
            {t('pages.billing.expenses.title')}
          </h3>
          {!compact ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('pages.billing.expenses.subtitle', { periodo })}
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{periodo}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
          <Button size="sm" onClick={openCreateModal}>
            {t('pages.billing.expenses.add')}
          </Button>
        </div>
      </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
          <Button size="sm" onClick={openCreateModal}>
            {t('pages.billing.expenses.add')}
          </Button>
        </div>
      )}

      {summary && !compact ? (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {[
            { label: t('pages.billing.expenses.cobrado'), value: summary.cobrado, border: 'border-green-500' },
            { label: t('pages.billing.expenses.gastado'), value: summary.gastado, border: 'border-orange-500' },
            { label: t('pages.billing.expenses.pendienteAprobacion'), value: summary.gastadoPendiente, border: 'border-yellow-500' },
            { label: t('pages.billing.expenses.resultado'), value: summary.resultado, highlight: true, border: 'border-teal-500' },
          ].map((card) => (
            <div
              key={card.label}
              className={`panel-stat !p-2.5 border-l-4 ${card.border}`}
            >
              <p className="panel-stat-label">{card.label}</p>
              <p
                className={`panel-stat-value !mt-0.5 text-base sm:text-lg ${
                  card.highlight
                    ? summary.resultado >= 0
                      ? 'text-teal-600 dark:text-teal-400'
                      : 'text-rose-600 dark:text-rose-400'
                    : ''
                }`}
              >
                {money(card.value)}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      {summary && compact && summary.gastosPendientes > 0 ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {t('pages.billing.expenses.pendingCount', {
            count: summary.gastosPendientes,
            monto: money(summary.gastadoPendiente),
          })}
        </p>
      ) : null}

      {showAddModal ? (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8 text-center">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={closeGastoModal}
            />
            <div className="relative inline-block w-full max-w-md rounded-xl bg-white dark:bg-gray-800 text-left shadow-xl p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {editingGasto
                      ? t('pages.billing.expenses.editModalTitle')
                      : t('pages.billing.expenses.modalTitle')}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('pages.billing.expenses.modalSubtitle', { periodo })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeGastoModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
                  aria-label={t('common.close')}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSaveGasto} className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">{t('pages.billing.expenses.nombre')}</label>
                  <Input
                    value={gastoForm.nombre}
                    onChange={(e) => setGastoForm((f) => ({ ...f, nombre: e.target.value }))}
                    autoFocus
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">{t('pages.billing.expenses.categoria')}</label>
                  <select
                    value={gastoForm.categoriaId}
                    onChange={(e) => {
                      const selected = categorias.find(
                        (categoria) => String(categoria.id) === e.target.value
                      );
                      setGastoForm((f) => ({
                        ...f,
                        categoriaId: e.target.value,
                        categoria: selected?.nombre || '',
                      }));
                    }}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    required
                  >
                    <option value="">Selecciona una categoría</option>
                    {categoriaLegacy ? (
                      <option value="__legacy">{categoriaLegacy}</option>
                    ) : null}
                    {categorias.map((categoria) => (
                      <option key={categoria.id} value={categoria.id}>
                        {categoria.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">{t('pages.billing.expenses.monto')}</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={gastoForm.monto}
                    onChange={(e) => setGastoForm((f) => ({ ...f, monto: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">{t('pages.billing.expenses.notas')}</label>
                  <Input
                    value={gastoForm.notas}
                    onChange={(e) => setGastoForm((f) => ({ ...f, notas: e.target.value }))}
                  />
                </div>
                {!editingGasto ? (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={gastoForm.esRecurrente}
                        onChange={(e) =>
                          setGastoForm((f) => ({ ...f, esRecurrente: e.target.checked }))
                        }
                      />
                      {t('pages.billing.expenses.esRecurrente')}
                    </label>
                    <p className="text-xs text-gray-500 pl-6">
                      {t('pages.billing.expenses.esRecurrenteHint')}
                    </p>
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={gastoForm.aprobarAlCrear}
                        onChange={(e) =>
                          setGastoForm((f) => ({ ...f, aprobarAlCrear: e.target.checked }))
                        }
                      />
                      {t('pages.billing.expenses.aprobarAlCrear')}
                    </label>
                  </div>
                ) : null}
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={closeGastoModal}
                    disabled={isSaving}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" size="sm" disabled={isSaving}>
                    {isSaving
                      ? t('common.saving')
                      : editingGasto
                        ? t('common.save')
                        : t('pages.billing.expenses.add')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      <ModalShell
        isOpen={Boolean(deletingGasto)}
        onClose={closeDeleteModal}
        title={t('pages.billing.expenses.deleteModalTitle')}
        subtitle={deletingGasto ? t('pages.billing.expenses.deleteModalSubtitle', {
          name: deletingGasto.nombre,
          amount: money(deletingGasto.monto),
        }) : undefined}
        size="md"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={closeDeleteModal}
              disabled={busyId === deletingGasto?.id}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="delete-expense-form"
              variant="danger"
              disabled={busyId === deletingGasto?.id}
              isLoading={busyId === deletingGasto?.id}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <form id="delete-expense-form" onSubmit={handleDeleteGasto} className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t('pages.billing.expenses.deleteModalBody')}
          </p>
          <div>
            <label
              htmlFor="delete-expense-reason"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('pages.billing.expenses.deleteReason')}
            </label>
            <textarea
              id="delete-expense-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              className="block min-h-[104px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t('pages.billing.expenses.deleteReasonPlaceholder')}
              maxLength={500}
              required
              autoFocus
            />
          </div>
        </form>
      </ModalShell>

      <div className={compact ? 'max-h-[280px] overflow-auto' : ''}>
        {/* Cards mobile */}
        <div className={`space-y-2 p-2 md:hidden ${compact ? '' : ''}`}>
          {sortedGastos.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-500">
              {isLoading ? t('common.loading') : t('pages.billing.expenses.empty')}
            </div>
          ) : (
            sortedGastos.map((g) => (
              <div key={g.id} className="panel-list-card px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                      {g.nombre}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                      {g.categoria}
                      {' · '}
                      {t(`pages.billing.expenses.origenVal.${g.origen}`)}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">
                      {money(g.monto)}
                    </p>
                    <span className="panel-chip mt-1 bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-slate-200">
                      {t(`pages.billing.expenses.estadoVal.${g.estado}`)}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap justify-end gap-1 border-t border-slate-100 pt-2 dark:border-gray-700/80">
                  {g.estado === 'pendiente' ? (
                    <>
                      <Button
                        size="sm"
                        disabled={busyId === g.id}
                        onClick={() => void handleAprobar(g.id)}
                      >
                        {t('pages.billing.expenses.approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busyId === g.id}
                        onClick={() => void handleRechazar(g.id)}
                      >
                        {t('pages.billing.expenses.reject')}
                      </Button>
                    </>
                  ) : null}
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busyId === g.id}
                    onClick={() => openEditModal(g)}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busyId === g.id}
                    onClick={() => openDeleteModal(g)}
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Tabla md+ */}
        <div
          className={`hidden overflow-auto rounded-xl border border-slate-200 dark:border-gray-700 md:block ${
            compact
              ? 'max-h-[280px] bg-slate-50/50 dark:bg-gray-900/20'
              : 'bg-white dark:bg-gray-800'
          }`}
        >
          <table className="min-w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[38%]" />
              <col className="w-[18%]" />
              <col className="w-[18%]" />
              <col className="w-[26%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs text-gray-500 dark:bg-gray-900">
              <tr>
                <th
                  className="cursor-pointer select-none px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('nombre')}
                >
                  {t('pages.billing.expenses.nombre')}
                  {sortIcon('nombre')}
                </th>
                <th
                  className="cursor-pointer select-none px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('monto')}
                >
                  {t('pages.billing.expenses.monto')}
                  {sortIcon('monto')}
                </th>
                <th
                  className="cursor-pointer select-none px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('estado')}
                >
                  {t('pages.billing.expenses.estado')}
                  {sortIcon('estado')}
                </th>
                <th className="px-2 py-1.5 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedGastos.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                    {isLoading ? t('common.loading') : t('pages.billing.expenses.empty')}
                  </td>
                </tr>
              ) : (
                sortedGastos.map((g) => (
                  <tr key={g.id} className="border-t border-slate-100 dark:border-gray-700">
                    <td className="px-2 py-1.5 text-gray-900 dark:text-white">
                      <div className="font-medium leading-tight">{g.nombre}</div>
                      <div className="text-[11px] text-gray-500">
                        {g.categoria} · {t(`pages.billing.expenses.origenVal.${g.origen}`)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-medium tabular-nums">
                      {money(g.monto)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {t(`pages.billing.expenses.estadoVal.${g.estado}`)}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap justify-end gap-1">
                        {g.estado === 'pendiente' ? (
                          <>
                            <Button
                              size="sm"
                              disabled={busyId === g.id}
                              onClick={() => void handleAprobar(g.id)}
                            >
                              {t('pages.billing.expenses.approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busyId === g.id}
                              onClick={() => void handleRechazar(g.id)}
                            >
                              {t('pages.billing.expenses.reject')}
                            </Button>
                          </>
                        ) : null}
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busyId === g.id}
                          onClick={() => openEditModal(g)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busyId === g.id}
                          onClick={() => openDeleteModal(g)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
